import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { isFormHasUnsavedChanges, isWarningAccepted } from '../helpers';
import history from '../history';
import warningMessages from '../helpers/warningMessages';

export default function(WrappedComponent) {
  @injectIntl
  @connect(state => ({ allForms: state.form }))
  class Wrap extends React.Component {
    static propTypes = {
      form: PropTypes.string.isRequired,
      intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
      }).isRequired,
      allForms: PropTypes.shape({}).isRequired,
      onClose: PropTypes.func,
      onSave: PropTypes.func,
      afterSave: PropTypes.func,
    };

    static defaultProps = {
      onClose: () => {},
      afterSave: () => {},
      onSave: () => {},
    };

    static childContextTypes = {
      checkImgLoading: PropTypes.func,
    };

    static contextTypes = {
      onEscapePress: PropTypes.func,
    };

    state = {
      formSaved: false,
    };

    unblock = null;

    getChildContext() {
      return {
        checkImgLoading: this.setImgLoading,
      };
    }

    componentDidMount() {
      if (this.context.onEscapePress) {
        this.context.onEscapePress(this.handleFormClose);
      }
      this.setListeners();
      window.addEventListener('remove_beforeunload', this.removeListeners);
    }

    componentWillUnmount() {
      this.removeListeners();
      this.setState({ formSaved: false });
      window.removeEventListener('remove_beforeunload', this.removeListeners);
    }

    onBeforeUnload = e => {
      let message;
      if (this.isPageUnsaved()) {
        message = this.getWarningMessage();
        e.returnValue = message;
      }
      return message;
    };

    getWarningMessage = () =>
      this.props.intl.formatMessage(
        warningMessages.onWindowRedirectWarningMessage,
      );

    setListeners = () => {
      window.addEventListener('beforeunload', this.onBeforeUnload);
      this.setHistoryListener();
    };

    setImgLoading = isLoading => {
      this.isImgLoading = isLoading;
    };

    setHistoryListener = () => {
      this.unblock = history.block(() => {
        let message;
        if (this.isPageUnsaved()) {
          message = this.getWarningMessage();
        }
        return message;
      });
    };

    isPageUnsaved = () => this.isImgLoading() || !this.isAllFormsSaved();

    isImgLoading = () => false;

    removeListeners = () => {
      this.unblock();
      window.removeEventListener('beforeunload', this.onBeforeUnload);
    };

    isCurrentFormCanExit = () => {
      if (this.state.formSaved) {
        return true;
      }
      const hasChanges = isFormHasUnsavedChanges({
        formName: this.props.form,
        allForms: this.props.allForms,
      });
      return (
        (!hasChanges && !this.isImgLoading()) ||
        isWarningAccepted(this.getWarningMessage())
      );
    };

    isAllFormsSaved = () => {
      const forms = Object.keys(this.props.allForms);

      if (forms.length === 1 && this.state.formSaved) {
        return true;
      }

      for (let i = 0; i < forms.length; i += 1) {
        if (
          isFormHasUnsavedChanges({
            formName: forms[i],
            allForms: this.props.allForms,
          })
        ) {
          return false;
        }
      }
      return true;
    };

    handleFormClose = () => {
      if (this.isCurrentFormCanExit()) {
        this.props.onClose();
      }
    };

    handleAfterSave = (...args) => {
      this.setState({ formSaved: true });
      if (this.props.afterSave) {
        this.props.afterSave(...args);
      }
    };

    handleOnSave = (...args) => {
      this.setState({ formSaved: true });
      if (this.props.onSave) {
        this.props.onSave(...args);
      }
    };

    render() {
      const { onClose, ...props } = this.props;
      return (
        <WrappedComponent
          formName={this.props.form}
          onClose={this.handleFormClose}
          {...props}
          afterSave={this.handleAfterSave}
          onSave={this.handleOnSave}
        />
      );
    }
  }

  return Wrap;
}
